/**
 * The voice call's dot grid: 22 columns whose lit height follows whoever is
 * speaking, and an idle wave when nobody is.
 *
 * Band volumes come from `@livekit/react-native`'s `useMultibandTrackVolume`,
 * the RN counterpart of the web hook — same bands, same 0..1 magnitudes, so
 * the geometry below is the web component's arithmetic unchanged.
 *
 * Two DOM mechanisms have RN equivalents rather than ports: the container's
 * ResizeObserver becomes `onLayout` (RN reports every layout pass anyway), and
 * the ambient wave's rAF loop becomes an interval — see `useAmbientWave`.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { Animated, StyleSheet, View, type LayoutChangeEvent, type StyleProp, type ViewStyle } from "react-native";

import { useMultibandTrackVolume } from "@livekit/react-native";
import type { LocalAudioTrack, RemoteAudioTrack } from "livekit-client";

import { solidColor } from "../lib/gradient";
import { ringgId } from "../lib/testing";
import { useWidgetTheme } from "../lib/widget-theme";

interface DotAudioVisualizerProps {
  localTrack?: LocalAudioTrack;
  remoteTrack?: RemoteAudioTrack;
  style?: StyleProp<ViewStyle>;
}

interface DotProps {
  lit: boolean;
  color: string;
}

interface DotColumnProps {
  volume: number;
  color: string;
  rows: number;
}

const BANDS = 22;
const MIN_ROWS = 5;
const MAX_ROWS = 21;
// Target vertical spacing per row — keeps density consistent across container heights.
const PX_PER_ROW = 22;

const DOT_SIZE = 8;
const UNLIT_OPACITY = 0.15;
const UNLIT_SCALE = 0.6;

/** framer's `{ type: "spring", stiffness: 220, damping: 22 }`, mass 1 implied. */
const DOT_SPRING = { stiffness: 220, damping: 22, mass: 1 };

/**
 * Idle wave, sampled instead of animated: no live track means no audio to
 * follow, so a slow sine stands in. 40ms is the same cadence LiveKit pushes
 * band updates at, which keeps the two sources feeling alike and costs a
 * fraction of the re-renders a frame loop would.
 *
 * The step is scaled to match: web advances 0.04 rad per rAF frame (~60Hz),
 * so the same wave speed at 25Hz needs 0.04 * 60/25. Carrying 0.04 across
 * unchanged would run the idle animation at 40% of the web widget's pace.
 */
const AMBIENT_TICK_MS = 40;
const WEB_STEP_PER_FRAME = 0.04;
const WEB_FRAMES_PER_SECOND = 60;
const AMBIENT_STEP = (WEB_STEP_PER_FRAME * WEB_FRAMES_PER_SECOND) / (1000 / AMBIENT_TICK_MS);
const AMBIENT_FLOOR = 0.15;
const AMBIENT_SWING = 0.25;
/** Phase offset per band — what makes the wave travel across the columns. */
const AMBIENT_PHASE = 0.5;

/** Row count from the measured height: `floor(h / 22)` clamped, forced odd. */
const rowsForHeight = (height: number): number => {
  const raw = Math.max(MIN_ROWS, Math.min(MAX_ROWS, Math.floor(height / PX_PER_ROW)));
  // Keep odd so there's a clear center row for the volume animation.
  return raw % 2 === 0 ? raw - 1 : raw;
};

const useResponsiveRows = (): { rows: number; onLayout: (event: LayoutChangeEvent) => void } => {
  const [rows, setRows] = useState(MIN_ROWS);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setRows(rowsForHeight(event.nativeEvent.layout.height));
  }, []);

  return { rows, onLayout };
};

/**
 * Only ticks while `enabled` — the wave is invisible during a live call, and
 * an interval nobody reads would still re-render the whole grid 25 times a
 * second.
 */
const useAmbientWave = (bands: number, enabled: boolean): number[] => {
  const [vals, setVals] = useState<number[]>(() => Array.from({ length: bands }, () => AMBIENT_FLOOR));

  useEffect(() => {
    if (!enabled) return;
    let t = 0;
    const id = setInterval(() => {
      t += AMBIENT_STEP;
      setVals(Array.from({ length: bands }, (_, i) => AMBIENT_FLOOR + AMBIENT_SWING * Math.abs(Math.sin(t + i * AMBIENT_PHASE))));
    }, AMBIENT_TICK_MS);
    return () => clearInterval(id);
  }, [bands, enabled]);

  return vals;
};

/**
 * Memoized because the grid is up to 462 dots and every volume tick re-renders
 * all of them; only the handful whose lit state actually flipped should touch
 * the animation driver.
 *
 * `lit` itself is not published anywhere — `testID` carries a name, not a
 * value, so web's `data-ringg-lit` has no counterpart and the state stays
 * visual.
 */
const Dot = memo(({ lit, color }: DotProps) => {
  // Mounts at its target — the first paint is a still frame, not an animation.
  const progress = useRef(new Animated.Value(lit ? 1 : 0)).current;

  useEffect(() => {
    const spring = Animated.spring(progress, { toValue: lit ? 1 : 0, ...DOT_SPRING, useNativeDriver: true });
    spring.start();
    return () => spring.stop();
  }, [lit, progress]);

  return (
    <Animated.View
      testID={ringgId("voice-animation-dot")}
      style={[
        styles.dot,
        {
          backgroundColor: color,
          // Spring overshoot is welcome on scale (it gives the pop) but must
          // not push opacity past opaque.
          opacity: progress.interpolate({ inputRange: [0, 1], outputRange: [UNLIT_OPACITY, 1], extrapolate: "clamp" }),
          transform: [{ scale: progress.interpolate({ inputRange: [0, 1], outputRange: [UNLIT_SCALE, 1] }) }],
        },
      ]}
    />
  );
});

const DotColumn: FC<DotColumnProps> = ({ volume, color, rows }) => {
  const centerIdx = Math.floor(rows / 2);
  // Perceived loudness curve — boosts low volumes so the column feels responsive.
  const perceived = Math.pow(Math.min(1, volume), 0.4);
  const litRadius = perceived * (centerIdx + 1);

  return (
    <View testID={ringgId("voice-animation-column")} style={styles.column}>
      {Array.from({ length: rows }, (_, i) => (
        <Dot key={i} lit={Math.abs(i - centerIdx) <= litRadius} color={color} />
      ))}
    </View>
  );
};

const DotAudioVisualizer: FC<DotAudioVisualizerProps> = ({ localTrack, remoteTrack, style }) => {
  const theme = useWidgetTheme();
  const dotColor = solidColor(theme.primaryColor);

  const localVols = useMultibandTrackVolume(localTrack, { bands: BANDS });
  const remoteVols = useMultibandTrackVolume(remoteTrack, { bands: BANDS });

  const hasLiveTrack = Boolean(localTrack || remoteTrack);
  const ambient = useAmbientWave(BANDS, !hasLiveTrack);

  // Whoever is louder owns the band, so one grid covers both sides of the call.
  const liveVolumes = useMemo(() => Array.from({ length: BANDS }, (_, i) => Math.max(localVols[i] ?? 0, remoteVols[i] ?? 0)), [localVols, remoteVols]);

  const volumes = hasLiveTrack ? liveVolumes : ambient;
  const { rows, onLayout } = useResponsiveRows();

  return (
    // Decorative, so hidden from both screen readers — the web leaf is
    // `aria-hidden`, which takes one prop per platform here.
    <View
      testID={ringgId("voice-animation")}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      onLayout={onLayout}
      style={[styles.container, style]}
    >
      {volumes.map((vol, i) => (
        <DotColumn key={i} volume={vol} color={dotColor} rows={rows} />
      ))}
    </View>
  );
};

export default DotAudioVisualizer;

const styles = StyleSheet.create({
  container: { flexDirection: "row", width: "100%", height: "100%", alignItems: "stretch", justifyContent: "space-around" },
  column: { flexDirection: "column", alignItems: "center", justifyContent: "space-around" },
  dot: { width: DOT_SIZE, height: DOT_SIZE, borderRadius: DOT_SIZE / 2 },
});
